import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import DimensionFilter from './DimensionFilter';
import FilterIcon from '@src/icons/filter-icon.svg';
import Icon, { PlusOutlined } from '@ant-design/icons';
import { FilterContainer, FilterCard, FilterTitle, FilterSeparator } from './styled';
import type { FilterCondition, FilterOperator } from '@src/shared/types/user-journeys';
export interface FilterConfig {
  key: string;
  operator?: FilterOperator;
  value: { value: string; label?: string }[];
}

export interface GlobalFilterProps {
  style?: React.CSSProperties;
  title?: string;
  showAddButton?: boolean;
  globalFilter?: FilterCondition[][];
  onFilterChange?: (filter: FilterCondition[][]) => void;
  interval: [string, string] | undefined;
}

const GlobalFilter: React.FC<GlobalFilterProps> = ({
  style,
  title,
  showAddButton = true,
  globalFilter,
  onFilterChange,
  interval,
}) => {
  const transformToFilterConditions = (values: FilterCondition[][]) => {
    const filters: FilterConfig[] = [];
    // FilterCondition[][] is array of OR groups, flatten to FilterConfig[]
    values.forEach((filterGroup) => {
      let key = '';
      let operator = 'equals';
      const value: { value: string; label?: string }[] = [];
      filterGroup.forEach((filter) => {
        key = filter.field;
        operator = filter.op;
        value.push({ value: filter.val, label: filter.label });
      });
      filters.push({
        key: key,
        operator: operator as FilterOperator,
        value,
      });
    });
    return filters;
  };

  const [filterConfigs, setFilterConfigs] = useState<FilterConfig[]>(
    Array.isArray(globalFilter) && globalFilter.length > 0
      ? transformToFilterConditions(globalFilter)
      : [{ key: '', operator: 'equals', value: [] }],
  );

  useEffect(() => {
    setFilterConfigs(
      Array.isArray(globalFilter) && globalFilter.length > 0
        ? transformToFilterConditions(globalFilter)
        : [{ key: '', operator: 'equals', value: [] }],
    );
  }, [globalFilter]);

  const transformFilterConfigs = (filters: FilterConfig[]): FilterCondition[][] => {
    const newFilters: FilterCondition[][] = [];

    // Add platform filter (hardcoded as web for now)

    // Convert globalFilter to new filters format
    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        const orGroup: FilterCondition[] = [];
        if (filter.key && filter.value && filter.value.length > 0) {
          // Create OR group for each filter value
          filter.value.forEach((value, index) => {
            orGroup.push({
              field: filter.key,
              op: filter.operator || 'equals',
              val: value.value,
              type: 'context',
              label: value.label,
            });
          });
        }
        newFilters.push(orGroup);
      });
    }
    return newFilters;
  };

  const handleDimensionKeyChange = (key: string, index: number) => {
    const newFilterConfigs = [...filterConfigs];
    newFilterConfigs[index].key = key;
    newFilterConfigs[index].value = [];
    setFilterConfigs(newFilterConfigs);
    // onFilterChange?.(newFilterConfigs);
  };

  const handleDimensionValuesChange = (
    values: { value: string; label: string }[],
    operator: FilterOperator,
    index: number,
  ) => {
    const newFilterConfigs = [...filterConfigs];
    newFilterConfigs[index].value = values;
    newFilterConfigs[index].operator = operator;
    setFilterConfigs(newFilterConfigs);
    if (values.length > 0) {
      onFilterChange?.(transformFilterConfigs(newFilterConfigs));
    }
  };

  const handleAddFilter = () => {
    const newFilter: FilterConfig = {
      key: ``,
      operator: 'equals',
      value: [],
    };
    setFilterConfigs([...filterConfigs, newFilter]);
    // onFilterChange?.([...filterConfigs, newFilter]);
  };

  const handleDelete = (index: number) => {
    const newFilterConfigs = [...filterConfigs];
    newFilterConfigs.splice(index, 1);
    setFilterConfigs(newFilterConfigs);
    onFilterChange?.(transformFilterConfigs(newFilterConfigs));
  };

  return (
    <FilterCard style={style}>
      {title && <FilterTitle>{title}</FilterTitle>}
      <FilterContainer>
        <Icon component={FilterIcon as any} style={{ fontSize: 16 }} />
        {filterConfigs?.map((filter, index) => (
          <>
            {index > 0 && <FilterSeparator>&amp;</FilterSeparator>}
            <DimensionFilter
              key={`${filter.key}-${index}`}
              dimensionKey={filter.key}
              dimensionValues={filter.value || []}
              dimensionOperator={filter.operator}
              interval={interval}
              onDimensionKeyChange={(v) => {
                handleDimensionKeyChange(v, index);
              }}
              onDimensionValuesChange={(v, operator) => {
                handleDimensionValuesChange(v, operator, index);
              }}
              onDelete={() => {
                handleDelete(index);
              }}
            />
          </>
        ))}

        {showAddButton && <Button icon={<PlusOutlined />} onClick={handleAddFilter}></Button>}
      </FilterContainer>
    </FilterCard>
  );
};

export default GlobalFilter;
